import React, { useContext, useState, useCallback } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useAgentContext } from '../hooks/useAgentContext';
import FloatingChat from '../components/common/FloatingChat';
import Breadcrumbs from '../components/common/Breadcrumbs';
import { Header } from '../components/layout/Header';

const AppLayout: React.FC = () => {
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const {
    messages,
    sendFeedbackToAgent,
    currentCaseDetails,
    isLoading,
    error: agentContextError,
  } = useAgentContext();

  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);

  const handleSendMessage = useCallback(
    async (message: string) => {
      if (!currentCaseDetails?.case_id) {
        setFeedbackError(
          'No active case selected. Please navigate to a specific business case or create a new one to use the chat.'
        );
        return;
      }

      setIsSendingFeedback(true);
      setFeedbackError(null);

      try {
        await sendFeedbackToAgent({
          caseId: currentCaseDetails.case_id,
          message: message,
        });
      } catch (err) {
        const errorObj =
          err && typeof err === 'object'
            ? (err as Record<string, unknown>)
            : {};
        const message = String(errorObj.message || '');
        setFeedbackError(
          message || 'Failed to send message. Please try again.'
        );
      } finally {
        setIsSendingFeedback(false);
      }
    },
    [currentCaseDetails, sendFeedbackToAgent]
  );

  // Only show chat on protected pages where user is authenticated
  const showChat =
    authContext?.currentUser &&
    !['/login', '/signup', '/'].includes(location.pathname);

  // Filter out PRD_DRAFT messages for the chat display
  const displayMessages = (messages || []).filter(
    (msg) => msg.messageType !== 'PRD_DRAFT'
  );

  return (
    <div className="flex flex-col min-h-screen">
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-0 focus:left-0 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded focus:border-2"
      >
        Skip to main content
      </a>

      {/* Header */}
      <Header />

      {/* Main Content */}
      <main
        id="main-content"
        className="flex-grow p-6 bg-white dark:bg-secondary-950"
        role="main"
      >
        <Breadcrumbs />
        <Outlet />
      </main>

      {/* Footer */}
      <footer
        className="p-4 mt-auto bg-secondary-100 dark:bg-secondary-900"
        role="contentinfo"
      >
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-sm text-secondary-600 dark:text-secondary-400">
            &copy; {new Date().getFullYear()} GraymatterLab
          </p>
          <nav
            className="flex items-center gap-4"
            aria-label="Legal navigation"
          >
            <Link
              to="/privacy"
              className="text-sm text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="text-secondary-400 dark:text-secondary-600">
              |
            </span>
            <Link
              to="/terms"
              className="text-sm text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Terms of Service
            </Link>
          </nav>
        </div>
      </footer>

      {/* Persistent Floating Chat */}
      {showChat && (
        <FloatingChat
          messages={displayMessages}
          onSendMessage={handleSendMessage}
          isSending={isSendingFeedback}
          isLoading={isLoading}
          error={feedbackError || agentContextError?.message || undefined}
          disabled={!currentCaseDetails}
          currentCaseTitle={currentCaseDetails?.title}
        />
      )}
    </div>
  );
};

export default AppLayout;
